import * as React from "react";
import { useNavigate } from "@tanstack/react-router";
import { useQueryClient } from "@tanstack/react-query";
import { Building2, Globe, Mail, MailCheck, RotateCcw, Save } from "lucide-react";
import { Callout } from "../../../ui/components/Callout";
import { useAuth } from "../../lib/auth/hooks";
import { MediaUploader } from "../../../ui/components/MediaUploader";
import type { MediaEntry } from "../../../ui/components/MediaUploader";
import * as userApi from "../../lib/api/users";
import { getApiErrorMessage } from "../../lib/api/api-error";
import { uploadOrgLogoFile } from "../../lib/profile-avatar-upload";
import {
  OrganizationDetailsFields,
  OrganizationContactFields,
  AutoApprovedEmailsField,
  emptyOrganizationFormValues,
  organizationToFormValues,
  type OrganizationFormValues,
  type OrganizationFieldChanges,
} from "./OrganizationFormFields";
import {
  organizationContactPayload,
  organizationDetailsPayload,
  organizationMembershipPayload,
  isContactDirty,
  isDetailsDirty,
  isMembershipDirty,
  type OrganizationSection,
} from "./organization-form-utils";
import { useI18n } from "../../lib/i18n";
import { useSectionCollapse } from "../../hooks/useSectionCollapse";
import { DetailPageLayout } from "../layouts/DetailPageLayout";
import { CollapsibleSection } from "../../../ui/components/CollapsibleSection";
import { UnsavedChangesChip } from "../../../ui/components/UnsavedChangesChip";
import type { FloatingAction } from "../../../ui/components/FloatingActionBar";
import * as styles from "./OrganizationEditPage.css";

const SECTION_IDS = {
  details: "org-edit-details",
  contact: "org-edit-contact",
  membership: "org-edit-membership",
} as const;

function buildDetailsChanges(
  values: OrganizationFormValues,
  saved: OrganizationFormValues,
): OrganizationFieldChanges {
  return {
    name: values.name.trim() !== saved.name.trim(),
    description: values.description.trim() !== saved.description.trim(),
    logoUrl: values.logoUrl.trim() !== saved.logoUrl.trim(),
  };
}

function buildContactChanges(
  values: OrganizationFormValues,
  saved: OrganizationFormValues,
): OrganizationFieldChanges {
  const phoneDirty =
    values.phoneCountryCode !== saved.phoneCountryCode ||
    values.phone.trim() !== saved.phone.trim();
  return {
    email: values.email.trim() !== saved.email.trim(),
    phoneCountryCode: phoneDirty,
    phone: phoneDirty,
    address: values.address.trim() !== saved.address.trim(),
    city: values.city.trim() !== saved.city.trim(),
    state: values.state.trim() !== saved.state.trim(),
    zip: values.zip.trim() !== saved.zip.trim(),
    country: values.country.trim() !== saved.country.trim(),
  };
}

export function OrganizationEditPage({ orgId }: { orgId: string }) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { t } = useI18n();
  const { accessToken } = useAuth();
  const token = accessToken ?? localStorage.getItem("access_token");
  const { isExpanded, toggle, bindSectionLayout } = useSectionCollapse("org-edit");

  const [values, setValues] = React.useState<OrganizationFormValues>(emptyOrganizationFormValues());
  const [savedValues, setSavedValues] = React.useState<OrganizationFormValues>(
    emptyOrganizationFormValues(),
  );
  const [slug, setSlug] = React.useState("");
  const [loading, setLoading] = React.useState(true);
  const [saving, setSaving] = React.useState(false);
  const [saveError, setSaveError] = React.useState<string | null>(null);

  const [logoError, setLogoError] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (!token || !orgId) return;
    setLoading(true);
    userApi
      .fetchOrganization(token, orgId)
      .then((org) => {
        const formValues = organizationToFormValues(org);
        setValues(formValues);
        setSavedValues(formValues);
        setSlug(org.slug);
      })
      .catch(() => {
        setSaveError("Failed to load organization");
      })
      .finally(() => setLoading(false));
  }, [token, orgId]);

  const patchValues = React.useCallback((patch: Partial<OrganizationFormValues>) => {
    setValues((prev) => ({ ...prev, ...patch }));
  }, []);

  const detailsDirty = isDetailsDirty(values, savedValues);
  const contactDirty = isContactDirty(values, savedValues);
  const membershipDirty = isMembershipDirty(values, savedValues);

  const detailsChanges = buildDetailsChanges(values, savedValues);
  const contactChanges = buildContactChanges(values, savedValues);

  const uploadLogoFile = React.useCallback(async (file: File): Promise<string> => {
    if (!token) throw new Error("Session expired. Please sign in again.");
    return uploadOrgLogoFile(token, file);
  }, [token]);

  const logoItems: MediaEntry[] = React.useMemo(
    () => (values.logoUrl.trim() ? [{ id: "logo", url: values.logoUrl.trim() }] : []),
    [values.logoUrl],
  );

  const handleLogoChange = React.useCallback((next: MediaEntry[]) => {
    patchValues({ logoUrl: next[0]?.url ?? "" });
  }, [patchValues]);

  // Logo persists immediately on Apply (matching avatar upload behavior), unlike the
  // other "Organization details" fields, which still wait for the page-level Save.
  const handleLogoApply = React.useCallback(async (newItems: MediaEntry[]) => {
    if (!token) throw new Error("Session expired. Please sign in again.");
    const newUrl = newItems[0]?.url ?? "";
    try {
      await userApi.updateOrganization(token, orgId, { logoUrl: newUrl || null });
    } catch (e) {
      throw new Error(getApiErrorMessage(e, "Failed to update logo"));
    }
    patchValues({ logoUrl: newUrl });
    setSavedValues((prev) => ({ ...prev, logoUrl: newUrl }));
    void queryClient.invalidateQueries({ queryKey: ['organizations', 'list'] });
  }, [token, orgId, patchValues, queryClient]);

  const handleResetAll = React.useCallback(() => {
    setValues(savedValues);
    setSaveError(null);
  }, [savedValues]);

  const handleSaveAll = React.useCallback(async () => {
    if (!token) {
      setSaveError("Session expired. Please sign in again.");
      return;
    }

    if (detailsDirty && !values.name.trim()) {
      setSaveError("Organization name is required.");
      return;
    }

    setSaving(true);
    setSaveError(null);

    try {
      if (detailsDirty) {
        await userApi.updateOrganization(
          token,
          orgId,
          organizationDetailsPayload(values, slug),
        );
      }
      if (contactDirty) {
        await userApi.updateOrganization(token, orgId, organizationContactPayload(values));
      }
      if (membershipDirty) {
        await userApi.updateOrganization(token, orgId, organizationMembershipPayload(values));
      }

      setSavedValues(values);
      void queryClient.invalidateQueries({ queryKey: ['organizations', 'list'] });
    } catch (e) {
      setSaveError(getApiErrorMessage(e, "Failed to save changes"));
    } finally {
      setSaving(false);
    }
  }, [token, orgId, slug, values, detailsDirty, contactDirty, membershipDirty, queryClient]);

  const detailsChangeCount =
    (detailsChanges.name ? 1 : 0) +
    (detailsChanges.description ? 1 : 0) +
    (detailsChanges.logoUrl ? 1 : 0);
  const contactChangeCount = Object.values(contactChanges).filter(Boolean).length;
  const membershipChangeCount = membershipDirty ? 1 : 0;
  const totalDirtyCount = detailsChangeCount + contactChangeCount + membershipChangeCount;
  const hasDirty = detailsDirty || contactDirty || membershipDirty;

  const floatingActions = React.useMemo((): FloatingAction[] => {
    if (!hasDirty) return [];
    return [
      {
        id: "reset",
        icon: <RotateCcw size={16} />,
        shape: "circle",
        tooltip: t("pro.productSettings.resetUnsaved"),
        disabled: saving,
        onClick: handleResetAll,
      },
      {
        id: "save",
        label:
          totalDirtyCount === 1
            ? `Save ${totalDirtyCount} change`
            : `Save ${totalDirtyCount} changes`,
        icon: <Save size={16} />,
        shape: "rectangle",
        loading: saving,
        disabled: saving,
        onClick: () => void handleSaveAll(),
      },
    ];
  }, [hasDirty, totalDirtyCount, saving, handleResetAll, handleSaveAll, t]);

  React.useEffect(() => {
    if (!hasDirty) return;
    const onBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, [hasDirty]);

  if (loading) {
    return (
      <div style={{ padding: "32px 24px" }}>
        <p style={{ color: "#6B7280" }}>{t("pro.orgs.loading")}</p>
      </div>
    );
  }

  const sectionLayout = bindSectionLayout([
    {
      id: SECTION_IDS.details,
      label: t("pro.orgs.organizationDetails"),
      icon: <Building2 size={14} />,
    },
    {
      id: SECTION_IDS.contact,
      label: t("pro.orgs.contact"),
      icon: <Mail size={14} />,
    },
    {
      id: SECTION_IDS.membership,
      label: t("pro.orgs.membershipEmailPatterns"),
      icon: <MailCheck size={14} />,
    },
  ]);

  return (
    <DetailPageLayout
      backLink={{
        label: "Back to organization",
        onClick: () => navigate({ to: "/workspace/organizations/$orgId", params: { orgId } }),
      }}
      header={{
        media: values.logoUrl.trim() ? (
          <img
            src={values.logoUrl.trim()}
            alt=""
            style={{
              width: 64,
              height: 64,
              borderRadius: 12,
              objectFit: "cover",
            }}
          />
        ) : (
          <Building2 size={28} color="#9CA3AF" aria-hidden />
        ),
        title: "Edit organization",
        subtitle: values.name.trim() || values.email.trim() || undefined,
      }}
      {...sectionLayout}
      floatingActions={floatingActions}
    >
      {saveError && (
        <div style={{ marginBottom: 16 }}>
          <Callout variant="error">{saveError}</Callout>
        </div>
      )}

      <CollapsibleSection
        id={SECTION_IDS.details}
        title={t("pro.orgs.organizationDetails")}
        icon={<Building2 size={20} />}
        expanded={isExpanded(SECTION_IDS.details)}
        onToggleExpanded={() => toggle(SECTION_IDS.details)}
        highlight={detailsDirty ? "accent" : false}
        statusIndicators={
          detailsDirty
            ? [<UnsavedChangesChip key="u" count={detailsChangeCount} />]
            : undefined
        }
      >
        <div className={styles.cardInner}>
          <OrganizationDetailsFields
            values={values}
            onChange={patchValues}
            changed={detailsChanges}
            logoUploader={
              <MediaUploader
                items={logoItems}
                onChange={handleLogoChange}
                onApply={handleLogoApply}
                uploadFile={uploadLogoFile}
                maxItems={1}
                shape="square"
                error={logoError}
                onError={setLogoError}
                placeholder={t("pro.orgs.logoPlaceholder")}
              />
            }
          />
        </div>
      </CollapsibleSection>

      <CollapsibleSection
        id={SECTION_IDS.contact}
        title={t("pro.orgs.contact")}
        icon={<Mail size={20} />}
        expanded={isExpanded(SECTION_IDS.contact)}
        onToggleExpanded={() => toggle(SECTION_IDS.contact)}
        highlight={contactDirty ? "accent" : false}
        statusIndicators={
          contactDirty
            ? [<UnsavedChangesChip key="u" count={contactChangeCount} />]
            : undefined
        }
      >
        <div className={styles.cardInner}>
          <OrganizationContactFields
            values={values}
            onChange={patchValues}
            changed={contactChanges}
          />
        </div>
      </CollapsibleSection>

      <CollapsibleSection
        id={SECTION_IDS.membership}
        title={t("pro.orgs.membershipEmailPatterns")}
        icon={<MailCheck size={20} />}
        expanded={isExpanded(SECTION_IDS.membership)}
        onToggleExpanded={() => toggle(SECTION_IDS.membership)}
        highlight={membershipDirty ? "accent" : false}
        statusIndicators={
          membershipDirty
            ? [<UnsavedChangesChip key="u" count={membershipChangeCount} />]
            : undefined
        }
      >
        <div className={styles.cardInner}>
          <AutoApprovedEmailsField
            patterns={values.emailPatterns}
            onChange={(emailPatterns) => patchValues({ emailPatterns })}
            changed={membershipDirty}
          />
        </div>
      </CollapsibleSection>
    </DetailPageLayout>
  );
}
